import { z } from "zod";

export const SharedBotSlugSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .min(3)
  .max(80);

export const SharedRepoSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/)
  .refine((v) => !v.endsWith("/.") && !v.endsWith("/.."));
export const SharedIntegrationSchema = z
  .object({
    serverId: z.string().min(1).max(100),
    provider: z.enum(["linear", "sentry", "posthog"]),
    resources: z.array(z.string().trim().min(1).max(160)).min(1).max(30),
    region: z.enum(["us", "eu"]).default("us"),
    ticketCreation: z
      .object({
        resource: z.string().regex(/^[A-Z][A-Z0-9]*$/),
        projectId: z.uuid(),
        assigneeId: z.uuid(),
        stateId: z.uuid(),
        creatorId: z.uuid(),
      })
      .strict()
      .optional(),
  })
  .superRefine((grant, ctx) => {
    if (
      grant.ticketCreation &&
      (grant.provider !== "linear" || !grant.resources.includes(grant.ticketCreation.resource))
    )
      ctx.addIssue({
        code: "custom",
        message: "Ticket creation must use an approved Linear team",
        path: ["ticketCreation"],
      });
    const pattern =
      grant.provider === "linear"
        ? /^[A-Z][A-Z0-9]*$/
        : grant.provider === "sentry"
          ? /^[a-z0-9_-]+\/[a-z0-9_-]+$/
          : /^[1-9][0-9]*$/;
    if (grant.resources.some((resource) => !pattern.test(resource)))
      ctx.addIssue({ code: "custom", message: "Invalid integration scope", path: ["resources"] });
  });
export type SharedIntegration = z.infer<typeof SharedIntegrationSchema>;
export const SharedIntegrationsSchema = z.array(SharedIntegrationSchema).max(12);
export const sharedIntegrationProviders = [
  {
    provider: "linear",
    name: "Linear",
    endpoint: "https://mcp.linear.app/mcp",
    scopeLabel: "Team keys",
    placeholder: "ENG, QA",
  },
  {
    provider: "sentry",
    name: "Sentry",
    endpoint: "https://mcp.sentry.dev/mcp",
    scopeLabel: "Organization/project",
    placeholder: "organization/web-app",
  },
  {
    provider: "posthog",
    name: "PostHog",
    endpoint: "https://mcp.posthog.com/mcp",
    scopeLabel: "Project IDs",
    placeholder: "12345",
  },
] as const;

export const SharedResourceQuerySchema = z.object({
  provider: z.enum(["github", "linear", "sentry", "posthog"]),
  serverId: z.string().min(1).max(100).optional(),
});
export const SharedResourceSchema = z.object({
  id: z.string().min(1).max(160),
  name: z.string().min(1).max(300),
  detail: z.string().max(300).optional(),
  region: z.enum(["us", "eu"]).default("us"),
});
export const SharedResourceListSchema = z.object({
  resources: z.array(SharedResourceSchema).max(5000),
});
export type SharedResource = z.infer<typeof SharedResourceSchema>;
export type SharedResourceQuery = z.infer<typeof SharedResourceQuerySchema>;
export type SharedResourceList = z.infer<typeof SharedResourceListSchema>;

/** Keep selected resources even when a source is temporarily unavailable. */
export function selectedSharedResources(
  grants: SharedIntegration[],
  serverId: string,
): SharedResource[] {
  return grants
    .filter((g) => g.serverId === serverId)
    .flatMap((g) => g.resources.map((id) => ({ id, name: id, region: g.region })));
}
export function toggleSharedResource(
  grants: SharedIntegration[],
  serverId: string,
  provider: SharedIntegration["provider"],
  resource: SharedResource,
  checked: boolean,
): SharedIntegration[] {
  const other = grants.filter((g) => g.serverId !== serverId || g.region !== resource.region);
  const existing = grants
    .filter((g) => g.serverId === serverId && g.region === resource.region)
    .flatMap((g) => g.resources);
  const resources = checked
    ? [...new Set([...existing, resource.id])]
    : existing.filter((id) => id !== resource.id);
  return [
    ...other,
    ...(resources.length
      ? [
          {
            ...grants.find((g) => g.serverId === serverId && g.region === resource.region),
            serverId,
            provider,
            region: resource.region,
            resources,
          },
        ]
      : []),
  ];
}

export const SharedBotInputSchema = z.object({
  slug: SharedBotSlugSchema.nullable().optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable()
    .optional(),
  password: z.string().min(8).max(128).nullable().optional(),
  name: z.string().trim().min(1).max(80),
  greeting: z.string().trim().max(300).nullable().optional(),
  instructions: z.string().max(16000).default(""),
  allowedRepos: z.array(SharedRepoSchema).max(30).default([]),
  readOnly: z.boolean().default(true),
  integrations: SharedIntegrationsSchema.optional(),
  enabled: z.boolean().default(false),
  hourlyLimit: z.number().int().min(1).max(200).default(30),
});
export const SharedBotSchema = SharedBotInputSchema.omit({ password: true }).extend({
  hasPassword: z.boolean(),
  id: z.string(),
  shareUrl: z.string(),
  updatedAt: z.string(),
});
export type SharedBot = z.infer<typeof SharedBotSchema>;
export type SharedBotInput = z.infer<typeof SharedBotInputSchema>;
const sharedLocator = {
  token: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  slug: SharedBotSlugSchema.optional(),
  access: z.string().max(1000).optional(),
};
const hasOneLocator = (value: { token?: string; slug?: string }) =>
  Boolean(value.token) !== Boolean(value.slug);
export const SharedChatInputSchema = z
  .object({
    ...sharedLocator,
    message: z.string().trim().min(1).max(8000),
    history: z
      .array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().max(16000) }))
      .max(20)
      .default([]),
  })
  .refine(hasOneLocator, "Provide one shared bot address")
  .refine(
    (v) => v.history.reduce((n, m) => n + m.content.length, 0) <= 60000,
    "Conversation is too long",
  );
export const SharedTokenSchema = z
  .object(sharedLocator)
  .refine(hasOneLocator, "Provide one shared bot address");
export const SharedUnlockSchema = z
  .object({ ...sharedLocator, password: z.string().max(128) })
  .refine(hasOneLocator, "Provide one shared bot address");

/** Owner-agent management never accepts passwords or tenant identifiers. */
export const SharedBotCreateToolSchema = z
  .object({
    settings: SharedBotInputSchema.omit({ password: true }).strict(),
  })
  .strict();
export const SharedBotUpdateToolSchema = z
  .object({
    bot_id: z.string().min(1).max(100),
    expected_version: z.string().regex(/^[a-f0-9]{64}$/),
    settings: z
      .object({
        name: SharedBotInputSchema.shape.name.optional(),
        greeting: SharedBotInputSchema.shape.greeting,
        slug: SharedBotInputSchema.shape.slug,
        color: SharedBotInputSchema.shape.color,
        instructions: SharedBotInputSchema.shape.instructions.removeDefault().optional(),
        allowedRepos: SharedBotInputSchema.shape.allowedRepos.removeDefault().optional(),
        readOnly: z.boolean().optional(),
        integrations: SharedIntegrationsSchema.optional(),
        enabled: z.boolean().optional(),
        hourlyLimit: SharedBotInputSchema.shape.hourlyLimit.removeDefault().optional(),
      })
      .strict(),
  })
  .strict();
export const SharedBotListToolSchema = z
  .object({ after: z.string().min(1).max(100).optional() })
  .strict();
export const SharedBotGetToolSchema = z.object({ bot_id: z.string().min(1).max(100) }).strict();

export const SharedBotResourceToolSchema = SharedResourceQuerySchema.extend({
  search: z.string().trim().max(200).optional(),
  offset: z.number().int().min(0).max(5000).optional(),
}).strict();
